import { useCallback, useEffect, useRef, useState } from 'react';
import { ANIMATION_DURATION } from '@optimus-web/common';
import { ScrollAwayProps, useClickAway } from '@optimus-web/helpers';

export const useDropdownState = (
    buttonRef: React.RefObject<HTMLButtonElement>,
    menuRef: React.RefObject<HTMLElement>,
    registerScrollAway: ScrollAwayProps['registerScrollAway'],
    deregisterScrollAway: ScrollAwayProps['deregisterScrollAway']
) => {
    const [opened, setOpened] = useState(false);
    const { registerClickAway, deregisterClickAway } = useClickAway();
    const controllerRef = useRef<AbortController | null>(null);

    const close = useCallback(
        ({ keepFocus = true } = {}) => {
            if (!opened) return;

            setOpened(false);
            deregisterClickAway();
            deregisterScrollAway();

            controllerRef.current?.abort();
            controllerRef.current = null;

            if (keepFocus && buttonRef.current) {
                buttonRef.current.focus();
            }
        },
        [deregisterClickAway, deregisterScrollAway, buttonRef, opened]
    );

    const focusAfterOpen = useCallback((getFocusItem: () => HTMLDivElement | null) => {
        const focusItem = getFocusItem();
        if (focusItem) {
            focusItem.focus();
        }
    }, []);

    const open = useCallback(
        ({ getFocusItem }: { getFocusItem: () => HTMLDivElement | null }) => {
            setOpened(true);

            controllerRef.current?.abort();
            controllerRef.current = new AbortController();
            const currentController = controllerRef.current;

            window.setTimeout(() => {
                if (menuRef.current) {
                    menuRef.current.addEventListener('transitionend', () => focusAfterOpen(getFocusItem), {
                        signal: currentController.signal,
                        once: true,
                    });
                }
            }, ANIMATION_DURATION.duration10);

            return () => {
                currentController.abort();
                controllerRef.current = null;
            };
        },
        [menuRef, focusAfterOpen]
    );

    useEffect(() => {
        if (opened) {
            registerClickAway({
                handler: () => close({ keepFocus: false }),
                roots: [() => menuRef.current],
            });
            registerScrollAway({
                handler: close,
                roots: [() => menuRef.current],
            });
        }
    }, [close, menuRef, opened, registerClickAway, registerScrollAway]);

    return { opened, open, close };
};
